import React from "react";
import { Box, Text, UnstyledButton } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { formatCurrency } from "../../../../../utils/cashDrawerUtils";
import { REVIEW_TAX_NOTE } from "../../booking/draftModel";
import {
  nameInitials,
  reviewDurationText,
  reviewSubText,
  reviewWhenText,
} from "./bookingSheetModel";
import type { MobileBookingDraft } from "./useMobileBookingDraft";
import styles from "./MobileBooking.module.css";

export interface MobileBookingReviewProps {
  draft: MobileBookingDraft;
  onEdit: () => void;
  error: string | null;
}

/**
 * Review step (README screen 5, addendum 2.11): client card, one read-only
 * card per service (WHEN / PROVIDER / DURATION), Subtotal, and the
 * `REVIEW_TAX_NOTE` (no tax estimate; checkout computes the real tax).
 */
export const MobileBookingReview: React.FC<MobileBookingReviewProps> = ({
  draft,
  onEdit,
  error,
}) => {
  return (
    <Box className={styles.reviewBody} data-testid="mobile-booking-review">
      <Box className={styles.clientCard}>
        <Box component="span" className={styles.revAvatar}>
          {nameInitials(draft.clientName)}
        </Box>
        <Box className={styles.headText}>
          <Text className={styles.revClient}>
            {draft.clientName || "No client selected"}
          </Text>
          <Text className={styles.revSub}>
            {reviewSubText(draft.rows.length, draft.day, draft.totals.minutes)}
          </Text>
        </Box>
      </Box>

      <Box className={styles.revLabelRow}>
        <Text component="span" className={styles.microLabel}>
          Services
        </Text>
        <UnstyledButton className={styles.editBtn} onClick={onEdit}>
          <IconPencil size={14} stroke={1.8} />
          <Box component="span">Edit services</Box>
        </UnstyledButton>
      </Box>

      <Box className={styles.cards}>
        {draft.rows.map((r) => {
          const svc = draft.serviceOf(r);
          const provider = draft.staffName(r.employeeId);
          return (
            <Box
              key={r.uid}
              className={styles.revCard}
              data-testid={`mobile-review-card-${r.uid}`}
            >
              <Box className={styles.revTop}>
                <Box
                  component="span"
                  className={styles.revDot}
                  style={
                    svc?.default_calendar_event_color
                      ? { background: svc.default_calendar_event_color }
                      : undefined
                  }
                />
                <Text component="span" className={styles.revName}>
                  {svc?.name || "No service"}
                </Text>
                <Text component="span" className={styles.revPrice}>
                  {formatCurrency(draft.priceOf(r))}
                </Text>
              </Box>
              <Box className={styles.revRows}>
                <Box className={styles.revRow}>
                  <Text component="span" className={styles.revMeta}>
                    When
                  </Text>
                  <Text component="span" className={styles.revValue}>
                    {reviewWhenText(r)}
                  </Text>
                </Box>
                <Box className={styles.revRow}>
                  <Text component="span" className={styles.revMeta}>
                    Provider
                  </Text>
                  <Text component="span" className={styles.revValue}>
                    {provider}
                  </Text>
                </Box>
                <Box className={styles.revRow}>
                  <Text component="span" className={styles.revMeta}>
                    Duration
                  </Text>
                  <Text component="span" className={styles.revValue}>
                    {reviewDurationText(r)}
                  </Text>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box className={styles.totals}>
        <Box className={styles.totalLine}>
          <Text component="span" className={styles.totalLabel}>
            Subtotal
          </Text>
          <Text component="span" className={styles.totalValue}>
            {formatCurrency(draft.totals.price)}
          </Text>
        </Box>
        <Text className={styles.collected}>{REVIEW_TAX_NOTE}</Text>
      </Box>

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}
    </Box>
  );
};
